import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function NorthYorkshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="North Yorkshire Pothole Claims | AMBER Across the Board on England's Biggest County Network | Fixtyer"
        description="North Yorkshire Council scores AMBER on every DfT measure. RED U-roads rose from 11% to 18% in two years, pothole work orders are up 55% since 2021/22, and the council admits it now completes less work than before 2020/21. See what that means for your claim."
        canonical="/councils/north-yorkshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'North Yorkshire', url: '/councils/north-yorkshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'North Yorkshire is AMBER on every DfT measure — what does that mean for my claim?',
            answer: 'It means the Department for Transport found nothing to fault badly — but nothing to praise either. Condition, spend and best practice are all AMBER: middling performance on England\u2019s sixth-largest surfaced network. For your claim, the headline rating matters less than the trends underneath it: RED-condition U-roads rose from 11% to 18% between 2022 and 2024, B and C roads in RED are up a third since 2022/23, and the council itself says reduced output "is beginning to have an impact on overall network condition."',
          },
          {
            question: 'What if my pothole was on one of the 4,930km of unclassified roads?',
            answer: 'U-roads make up 53% of North Yorkshire\u2019s network — 4,930km of rural lanes, village streets and estate roads. They are not surveyed with SCANNER laser equipment like A, B and C roads; the council uses an AI camera-based Coarse Visual Inspection, which its own report describes as "less detailed than the SCANNER survey". The council only aims to survey a minimum of 70% of the U-road network each year, and 18% of U-roads were in RED condition in 2024 — roughly 887km of road the council\u2019s own data says should be considered for maintenance.',
          },
          {
            question: 'Does the 2023 local government reorganisation help my claim?',
            answer: 'Partially — and we\u2019ll be straight about it. North Yorkshire Council was formed on 1 April 2023, replacing North Yorkshire County Council and seven district councils. Unlike some new unitaries, highways were already a county-level function, so inspection records did not start from zero. But all pre-April-2023 condition data, inspection records and policies belong to the predecessor county council, and the new authority took over a 9,275km network in the same period its own report says conditions began slipping. If the council relies on pre-2023 records for a Section 58 defence, it must show those predecessor systems transferred intact — a fair question to ask in disclosure.',
          },
          {
            question: 'The council filled 11,015 potholes in 2024/25 — doesn\u2019t that prove the system works?',
            answer: 'Read the small print. The council\u2019s own footnote says those figures are "the number of work orders raised for pothole repairs" and that "each work order contains multiple pothole or actionable surface defect repairs." The true number of individual potholes repaired is a multiple of 11,015. Work orders are also up 55% since 2021/22 — a network generating defects that fast is one where potholes routinely form between inspections, which is exactly where prior reports and photographic evidence win claims.',
          },
          {
            question: 'The council blames inflation for doing less work — is that a defence?',
            answer: 'No. Section 58 of the Highways Act 1980 asks whether the council took such care as was reasonably required to keep the specific road safe — budget pressure is not a statutory defence. If anything, the council\u2019s admission that "the amount of work we have been able to complete in recent years is lower than the period before 2020 to 2021" and that this "is beginning to have an impact on overall network condition" is documented knowledge that the network is deteriorating. Knowing that, and not adjusting inspection or repair regimes accordingly, weakens a reasonableness defence rather than supporting one.',
          },
          {
            question: 'How current is North Yorkshire\u2019s road condition data?',
            answer: 'Less current than the tables suggest. Since 2021/22, SCANNER data for A, B and C roads has been collected over a two-year cycle — 50% of the network in one direction in year one, the opposite direction in year two — so each published year blends two summers of surveying. The council\u2019s own example: the 2022/23 figure combines data from summer 2021 and summer 2022. On a deteriorating network, that lag means the published condition picture can flatter the road you actually drove on.',
          },
        ]}
      />
      <ServiceSchema 
        councilName="North Yorkshire Council" 
        councilSlug="north-yorkshire" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/north-yorkshire"
        headline="North Yorkshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">North Yorkshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
            <span className="px-3 py-1 bg-blue-600 text-white font-bold rounded-full text-sm">NEW AUTHORITY 2023</span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            North Yorkshire: AMBER Across the Board on England's Biggest County Network
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            North Yorkshire Council runs <strong className="text-white">9,275km of roads</strong> — England's 
            sixth-largest surfaced network — and scores <strong className="text-amber-400">AMBER on every single 
            DfT measure</strong>: condition, spend and best practice. Underneath the middling scorecard, 
            RED-condition U-roads jumped from <strong className="text-rose-400">11% to 18% in two years</strong>, 
            pothole work orders are up 55% since 2021/22, and the council admits its reduced output 
            "is beginning to have an impact on overall network condition."
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">11,015</div>
            <div className="text-white font-medium mt-2">Pothole work orders in 2024/25 alone</div>
            <div className="text-brand-300 text-sm mt-1">
              And each work order "contains multiple pothole or actionable surface defect repairs" — 
              the council's own footnote. The real pothole count is a multiple of this figure.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Network */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Scale Problem: Harrogate to New Delhi
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own comparison — its surfaced network is roughly the distance you'd 
            drive from Harrogate to New Delhi
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road type</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of network</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center font-mono">934km</td>
                  <td className="px-6 py-4 text-center">10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono">3,411km</td>
                  <td className="px-6 py-4 text-center">37%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-semibold text-brand-900">Unclassified (U) roads</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">4,930km</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">53%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total roads</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">9,275km</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-brand-900">6th</div>
              <div className="text-sm text-surface-600 mt-1">Largest surfaced network in England — 8,600km surfaced</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-brand-900">~2,000</div>
              <div className="text-sm text-surface-600 mt-1">Bridges and structures, some centuries old</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-brand-900">1,500</div>
              <div className="text-sm text-surface-600 mt-1">Towns, villages and hamlets connected by the network</div>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why Scale Cuts Both Ways</h3>
            <p className="text-brand-200 mb-4">
              A vast, predominantly rural network is the council's standard explanation for stretched 
              resources — and it's true. But scale is a double-edged sword under Section 58:
            </p>
            <ul className="text-brand-300 space-y-2">
              <li>• The duty under Section 41 of the Highways Act 1980 applies to every metre of those 9,275km equally</li>
              <li>• A "reasonable system" must cover remote rural lanes as well as town centres — the law does not discount the Dales</li>
              <li>• The bigger the network, the longer the practical gap between inspections on minor roads — and the more defects form unseen in between</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from North Yorkshire's own transparency report — and every 
            road class turned downward in 2024/25
          </p>

          {/* A-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (934km): green share falling</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">20.6%</td>
                    <td className="px-4 py-2 text-center">76.4%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">2.9%</td>
                    <td className="px-4 py-2 text-center">20.4%</td>
                    <td className="px-4 py-2 text-center">76.7%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">2.6%</td>
                    <td className="px-4 py-2 text-center">19.2%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">78.2%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">2.6%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">76.4%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold">2.8%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">24.1%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">73.1%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-roads in good condition fell from 78.2% to 73.1% in just two years, while the AMBER 
              share — roads where "maintenance may be required soon" — climbed from 19.2% to 24.1%. 
              That's the pipeline of tomorrow's potholes filling up.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (3,411km): RED up a third since 2022/23</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">3.3%</td>
                    <td className="px-4 py-2 text-center">27.4%</td>
                    <td className="px-4 py-2 text-center">69.3%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">2.9%</td>
                    <td className="px-4 py-2 text-center">26.6%</td>
                    <td className="px-4 py-2 text-center">70.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">2.7%</td>
                    <td className="px-4 py-2 text-center">25.4%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">71.9%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">26.2%</td>
                    <td className="px-4 py-2 text-center">70.8%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3.6%</td>
                    <td className="px-4 py-2 text-center font-bold">28%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">68.4%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B and C roads rose a third in two years (2.7% → 3.6%), and the green share 
              fell from 71.9% to 68.4% — its lowest in the five-year table. On a 3,411km B/C network, 
              every percentage point of RED is roughly 34km of road that "should be considered for maintenance".
            </p>
          </div>

          {/* The two-year survey cycle */}
          <div className="bg-white rounded-xl p-6 border border-surface-200">
            <h3 className="font-semibold text-brand-900 mb-3">The Two-Summer Blend</h3>
            <p className="text-surface-600 text-sm mb-3">
              Since 2021/22, SCANNER data has been collected over a two-year cycle: 50% of the network 
              in one direction in year one, the opposite direction in year two. The council's own example: 
              "data for 2022/23 is made up of data collected in summer 2021 and summer 2022."
            </p>
            <p className="text-surface-600 text-sm">
              On a deteriorating network, that means the published figures always lag reality — the 
              2024/25 numbers partly describe roads as they were in summer 2023. If the trend is downward, 
              the road you actually hit is likely worse than the table says.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road problem */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            4,930km of U-Roads — RED Up From 11% to 18% in Two Years
          </h2>
          <p className="text-surface-600 mb-8">
            More than half the network is unclassified — surveyed by camera, not laser, with a 70% coverage target
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Survey year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Approx. length</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold">20%</td>
                  <td className="px-6 py-4 text-center text-surface-500">~986km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold">17%</td>
                  <td className="px-6 py-4 text-center text-surface-500">~838km</td>
                </tr>
                <tr className="border-b border-surface-100 bg-emerald-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">11%</td>
                  <td className="px-6 py-4 text-center text-surface-500">~542km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold">14%</td>
                  <td className="px-6 py-4 text-center text-surface-500">~690km</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">~887km</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">A Sharp Reversal</h3>
              <p className="text-surface-600 text-sm mb-3">
                Between 2016 and 2021, extra investment from the York and North Yorkshire Local Enterprise 
                Partnership and the council itself improved rural C and U roads — and the data shows it: 
                RED U-roads fell from 20% to 11% by 2022.
              </p>
              <p className="text-surface-600 text-sm">
                Then the funding ended and inflation hit. RED U-roads have climbed back to <strong>18% — 
                roughly 887km of unclassified road</strong> the council's own surveys flag as needing 
                consideration for maintenance. That's a 64% rise in the RED share in just two years.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">A Less Detailed Survey, Partial Coverage</h3>
              <p className="text-amber-800 text-sm mb-3">
                U-roads are not surveyed with SCANNER lasers. The council uses the Vaisala Road AI 
                camera system, "based on a Coarse Visual Inspection survey, which is less detailed 
                than the SCANNER survey" — the report's own words.
              </p>
              <p className="text-amber-800 text-sm">
                And coverage is not total: the council states it aims "to survey a minimum of 70% of 
                the U road network each year." Up to 1,479km of U-road can legitimately go unsurveyed 
                in any given year under that target.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, the council must show a reasonable system for knowing 
              and managing the condition of the road that damaged your vehicle. On North Yorkshire's 
              U-road network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road in the 70% surveyed that year — or the portion that wasn't?</li>
              <li>• If 18% of U-roads were RED in 2024, what was done about yours specifically?</li>
              <li>• A coarse camera survey is "less detailed" by the council's own admission — what did it miss?</li>
              <li>• With RED U-roads rising 64% in two years, was inspection frequency increased to match?</li>
            </ul>
            <p className="text-white font-medium">
              A council whose own data shows accelerating decline on half its network has a documented 
              reason to inspect more, not less.
            </p>
          </div>
        </div>
      </section>

      {/* The inflation admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Council's Own Admission
          </h2>
          <p className="text-surface-600 mb-8">
            Buried in the "additional information on condition" section — in the council's own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="Since 2021, inflationary pressures have resulted in significant increases in costs for highway maintenance activities. While the rate of inflation has slowed recently, costs are still significantly higher than in 2020 to 2021. As such, the amount of work we have been able to complete in recent years is lower than the period before 2020 to 2021. This is beginning to have an impact on overall network condition."
              source="North Yorkshire Council, Information About Our Highway Maintenance (DfT transparency report, 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Admission Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                This is North Yorkshire formally acknowledging two things at once: it is completing 
                less maintenance work than it did before 2020/21, and network condition is suffering 
                as a result. The condition tables back it up — every road class declined in 2024/25.
              </p>
              <p className="text-surface-600 text-sm">
                Under Section 58, documented knowledge of declining condition raises the bar for what 
                a "reasonable" inspection and repair regime looks like. A council that knows its network 
                is deteriorating cannot reasonably carry on as if it weren't.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">And Inflation Is Not a Defence</h3>
              <p className="text-amber-800 text-sm mb-3">
                Section 58 turns on whether the council took such care as was reasonably required to 
                keep the highway safe for traffic. Budget pressures, inflation and funding shortfalls 
                are not among the statutory considerations.
              </p>
              <p className="text-amber-800 text-sm">
                Courts have consistently held that lack of resources does not lower the standard of 
                the Section 41 duty. The admission explains the decline — it does not excuse it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Repairs and spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Pothole Work Orders Up 55% Since 2021/22
          </h2>
          <p className="text-surface-600 mb-8">
            45,323 work orders in five years — and each one covers multiple potholes
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Pothole work orders</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2021/22</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">7,266</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">7,085</td>
                  <td className="px-6 py-4 text-center text-surface-400">baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">8,079</td>
                  <td className="px-6 py-4 text-center">+14%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11,878</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+68%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold">11,015</td>
                  <td className="px-6 py-4 text-center font-bold">+55%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">45,323</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Multiplier Caveat</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council's own footnote: "The figures quoted are for the number of work orders raised 
                for pothole repairs. Each work order contains multiple pothole or actionable surface 
                defect repairs."
              </p>
              <p className="text-amber-800 text-sm">
                So 45,323 is not the pothole count — it's the lower bound on a number that's some 
                multiple higher. The network is producing defects considerably faster than the 
                headline figures suggest.
              </p>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Repeat-Repair Admission</h3>
              <p className="text-surface-600 text-sm mb-3">
                In its 2025/26 plans, the council says it is "reviewing how we repair these defects 
                with an increased focus on longer term repairs to reduce the number of return or 
                repeat repairs."
              </p>
              <p className="text-surface-600 text-sm">
                You don't review your repair methods to reduce repeat repairs unless repeat repairs 
                are a problem. If the pothole that damaged your vehicle had been patched before, 
                that history is worth establishing — a failed previous repair is strong evidence.
              </p>
            </div>
          </div>

          {/* Spending */}
          <h3 className="font-display text-2xl font-bold text-brand-950 mb-4">
            The Money: £4.8 Million a Year Just Filling Potholes
          </h3>
          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative share</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£49.0m</td>
                  <td className="px-4 py-3 text-center text-rose-600">£45.4m</td>
                  <td className="px-4 py-3 text-center">63%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£46.6m</td>
                  <td className="px-4 py-3 text-center">£46.7m</td>
                  <td className="px-4 py-3 text-center">62%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£39.9m</td>
                  <td className="px-4 py-3 text-center">£42.6m</td>
                  <td className="px-4 py-3 text-center text-rose-600">57%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£51.4m</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">£49.8m</td>
                  <td className="px-4 py-3 text-center">61%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£44.8m</td>
                  <td className="px-4 py-3 text-center">£50.5m</td>
                  <td className="px-4 py-3 text-center">61%</td>
                </tr>
                <tr className="bg-surface-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center font-bold">£60.8m</td>
                  <td className="px-4 py-3 text-center font-bold">£62.0m</td>
                  <td className="px-4 py-3 text-center font-bold">65%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Why Spend Is Only AMBER</h3>
              <p className="text-surface-600 text-sm">
                Unlike GREEN-rated councils that consistently spend well above their allocation, North 
                Yorkshire's record is mixed: it underspent its DfT allocation in 2020/21 (£45.4m against 
                £49.0m) and again in 2023/24 (£49.8m against £51.4m) — the new council's first full year. 
                The preventative share dipped to 57% in 2022/23 before recovering to a projected 65%.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">£4.8m a Year on Reactive Pothole Repairs</h3>
              <p className="text-amber-800 text-sm">
                The council estimates it spends £4.8 million per year on reactive revenue repairs to 
                potholes — 15% of its reactive budget and 6% of its entire highway budget. That's the 
                running cost of a network that generates over 11,000 pothole work orders a year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The 2023 reorganisation */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 2023 Reorganisation: What It Does — and Doesn't — Mean
          </h2>
          <p className="text-surface-600 mb-8">
            North Yorkshire Council formed on 1 April 2023, replacing the county council and seven districts
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Honest Picture</h3>
              <p className="text-surface-600 text-sm mb-3">
                We'll be straight: this is not Cumberland. Highways were already a county-level function, 
                so when North Yorkshire County Council and seven district councils merged into the new 
                unitary, the highway authority's inspection records and systems had institutional 
                continuity. The council cannot be accused of starting from zero.
              </p>
              <p className="text-surface-600 text-sm">
                All condition and spending data before April 2023 in the tables above belongs to the 
                predecessor county council — including the 2016-2021 rural roads investment that 
                produced the now-reversed improvement.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">What's Still Worth Probing</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• If the council relies on pre-2023 inspection records for a Section 58 defence, it must show those records and policies transferred intact through the reorganisation — ask in disclosure</li>
                <li>• The new authority's first full year (2023/24) saw an underspend against the DfT allocation and the biggest jump in pothole work orders in the five-year table (+47% year on year)</li>
                <li>• The timing is awkward for the council: it took over precisely as its own report says conditions began to slip</li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Liability Didn't Change Hands — It Continued</h3>
            <p className="text-brand-200 mb-4">
              Whatever the structure, the Section 41 duty to maintain ran continuously through 
              1 April 2023. For claims either side of that date, North Yorkshire Council answers 
              for the network — and for the completeness of the records it inherited.
            </p>
            <p className="text-white font-medium">
              "That predates us" is not a defence. If the records exist, they must be produced; 
              if they don't, the Section 58 defence has a hole in it.
            </p>
          </div>
        </div>
      </section>

      {/* Strengths and weaknesses */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an All-AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: North Yorkshire is neither a basket case nor a model — here's how that shapes your claim
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual SCANNER surveys on all A, B and C roads — a credible condition-monitoring regime for classified routes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A documented risk-based, lifecycle asset management strategy
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> In-house delivery through NY Highways since 2021, with AI survey data processed in under four hours
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Spending above allocation in 2024/25 and projected for 2025/26, with the preventative share rising to 65%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road RED share is genuinely low (2.8%) by national standards
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a competent, documented Section 58 defence on A-roads. Generic claims will struggle there.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER on all four DfT measures — no GREEN anywhere on the scorecard
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED U-roads up from 11% to 18% in two years — ~887km of flagged road
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-roads (53% of the network) surveyed by a "less detailed" camera method, with only a 70% annual coverage target
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Pothole work orders up 55% since 2021/22 — each covering multiple potholes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> The council's own admission that reduced output "is beginning to have an impact on overall network condition"
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Condition data lags up to two years behind reality on classified roads
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy: Road Class and Timing</h3>
            <p className="text-brand-200 mb-4">
              Against an all-AMBER council, the strength of your claim depends heavily on 
              <strong className="text-white"> where and when</strong> you hit the defect:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• On a U-road, lead with the survey-method gap: coarse camera surveys, 70% coverage, and a RED share that rose 64% in two years</li>
              <li>• On a B or C road, the 2024/25 decline and two-summer data lag are your structural arguments</li>
              <li>• Anywhere, prior reports of the same defect (FixMyStreet, council reports) convert a difficult claim into a strong one — actual notice defeats Section 58</li>
              <li>• Photographs showing weathered edges or previous patching tie your defect to the council's admitted repeat-repair problem</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, 
              and cites North Yorkshire's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in North Yorkshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            England's biggest county network demands a properly built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No use of the council's own admissions</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ RED U-road surge (11% → 18%) documented</li>
                <li>✅ 70% survey coverage gap argued</li>
                <li>✅ 45,323 work orders in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to North Yorkshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=north-yorkshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My North Yorkshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                North Yorkshire is AMBER on every DfT measure — what does that mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It means the Department for Transport found nothing to fault badly — but nothing to praise 
                either. Condition, spend and best practice are all AMBER: middling performance on England's 
                sixth-largest surfaced network. For your claim, the headline rating matters less than the 
                trends underneath it: RED-condition U-roads rose from 11% to 18% between 2022 and 2024, 
                B and C roads in RED are up a third since 2022/23, and the council itself says reduced 
                output "is beginning to have an impact on overall network condition."
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on one of the 4,930km of unclassified roads?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 53% of North Yorkshire's network — 4,930km of rural lanes, village streets 
                and estate roads. They are not surveyed with SCANNER laser equipment like A, B and C roads; 
                the council uses an AI camera-based Coarse Visual Inspection, which its own report describes 
                as "less detailed than the SCANNER survey". The council only aims to survey a minimum of 70% 
                of the U-road network each year, and 18% of U-roads were in RED condition in 2024 — roughly 
                887km of road the council's own data says should be considered for maintenance.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the 2023 local government reorganisation help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Partially — and we'll be straight about it. North Yorkshire Council was formed on 
                1 April 2023, replacing North Yorkshire County Council and seven district councils. Unlike 
                some new unitaries, highways were already a county-level function, so inspection records did 
                not start from zero. But all pre-April-2023 condition data, inspection records and policies 
                belong to the predecessor county council, and the new authority took over a 9,275km network 
                in the same period its own report says conditions began slipping. If the council relies on 
                pre-2023 records for a Section 58 defence, it must show those predecessor systems transferred 
                intact — a fair question to ask in disclosure.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The council filled 11,015 potholes in 2024/25 — doesn't that prove the system works?
              </h3>
              <p className="text-surface-600 text-sm">
                Read the small print. The council's own footnote says those figures are "the number of work 
                orders raised for pothole repairs" and that "each work order contains multiple pothole or 
                actionable surface defect repairs." The true number of individual potholes repaired is a 
                multiple of 11,015. Work orders are also up 55% since 2021/22 — a network generating defects 
                that fast is one where potholes routinely form between inspections, which is exactly where 
                prior reports and photographic evidence win claims.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The council blames inflation for doing less work — is that a defence?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Section 58 of the Highways Act 1980 asks whether the council took such care as was 
                reasonably required to keep the specific road safe — budget pressure is not a statutory 
                defence. If anything, the council's admission that "the amount of work we have been able to 
                complete in recent years is lower than the period before 2020 to 2021" and that this "is 
                beginning to have an impact on overall network condition" is documented knowledge that the 
                network is deteriorating. Knowing that, and not adjusting inspection or repair regimes 
                accordingly, weakens a reasonableness defence rather than supporting one.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How current is North Yorkshire's road condition data?
              </h3>
              <p className="text-surface-600 text-sm">
                Less current than the tables suggest. Since 2021/22, SCANNER data for A, B and C roads has 
                been collected over a two-year cycle — 50% of the network in one direction in year one, the 
                opposite direction in year two — so each published year blends two summers of surveying. The 
                council's own example: the 2022/23 figure combines data from summer 2021 and summer 2022. 
                On a deteriorating network, that lag means the published condition picture can flatter the 
                road you actually drove on.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.northyorks.gov.uk/roads-parking-and-travel/information-about-our-highway-maintenance"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              North Yorkshire Council — Information About Our Highway Maintenance (2025)
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
